import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import config from '../components/config/config';
import * as R from 'ramda';
import TokenManagerService from 'App/services/TokenManagerService';

class SessionStorageService {
    /**
     * @summary Gets the provided key, and will return the result as an object if json is set true;
     *
     * @description
     * This differs from get(). Get retrieves keys that are prefixed by the user token. This retrieves keys that
     * are prefixed by the user id.
     *
     * @param {string} key - The key to lookup in local storage.
     * @param {boolean} json - Whether to return the result as JSON or not
     * @deprecated All values should be available through context: React query, etc. Since Angular is dead, we no longer need this.
     * @returns {string|object|null}
     */
    static getOnUser = (key, json) => {
        if (typeof key !== 'string') {
            throw new Error(`Invalid key provided expected type string; got type ${typeof key} ${key}`);
        }

        const userId = window.localStorage.getItem('userId');
        if (userId) {
            const value = window.localStorage.getItem(`${userId}_${key}`);
            if (json === true) {
                try {
                    return typeof value === 'string' ? JSON.parse(value) : null;
                } catch (_e) {
                    window.localStorage.removeItem(`${userId}_${key}`);
                }
            }
            return value;
        }
        return null;
    };

    /**
     * @summary Sets a value in local storage
     *
     * @description
     * Takes the provided key and prepends it with the user id, if the provided `value` is an object
     * it will be JSON stringified to enable its storage in local storage
     *
     * @param {string} key - Key to store the value by
     * @param {any} value - Value to be stored.
     * @deprecated All values should be available through context: React query, etc. Since Angular is dead, we no longer need this.
     * @returns void
     */
    static setOnUser(key, value) {
        if (typeof key !== 'string') {
            throw new Error(`Invalid key provided expected type string; got type ${typeof key} ${key}`);
        }
        if (typeof value === 'object') {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line no-param-reassign
            value = JSON.stringify(value);
        }

        const userId = window.localStorage.getItem('userId');
        if (userId) {
            return window.localStorage.setItem(`${userId}_${key}`, value);
        }
        throw new Error('No user id found for this user');
    }

    /**
     * Gets the domain.tld
     * @returns {string}
     */
    static getCookieDomain() {
        const [tld, domain] = config.therigyEndpoint
            .replace(/(https?:\/\/|\/)/, '')
            .split('.')
            .reverse();

        return `${domain}.${tld}`;
    }

    /**
     * @summary Saves JWT in a cross-browser manner
     *
     * @param {string} token - JWT
     * @returns {null}
     */
    static setJwt = (token) => {
        TokenManagerService.setTokens({ access: token });
    };

    static clearSession() {
        localStorage.clear();
    }

    static getSsoCookie() {
        return Cookies.get('sso_jwt');
    }

    /**
     * @summary Retrieves JWT in a cross-browser manner
     *
     * @returns {string}
     */
    static getJwt = () => {
        // If the user is arriving from SSO get the JWT out of the cookie.
        const ssoCookie = Cookies.get('sso_jwt');
        if (ssoCookie) {
            const domain = `.${SessionStorageService.getCookieDomain()}`;
            Cookies.remove('sso_jwt', { path: '/', domain });
            const { data } = jwtDecode(ssoCookie);
            Cookies.set('sessid', data.Token);
            window.localStorage.setItem('userId', data.UserID);
            window.localStorage.setItem('branchId', data.BranchID);
            window.localStorage.setItem('companyId', data.CompanyID);
            window.localStorage.setItem('userAge', data.UserAge);
            this.setJwt(ssoCookie);
            window.localStorage.setItem('sessionId', Cookies.get('sessid'));
            return ssoCookie;
        }

        return TokenManagerService.getAccessToken();
    };

    static getDecodedJwt = () =>
        R.compose(
            R.ifElse(R.either(R.isNil, R.isEmpty), R.always({}), (d) => jwtDecode(d)),
            SessionStorageService.getJwt
        )();
}

export default SessionStorageService;
